import React, { useState, useEffect, useId, useRef } from 'react';
import { useDispatch } from 'react-redux';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { Form, Field } from 'react-final-form';
import omit from 'lodash.omit';
import { DateTime } from 'luxon';
import { AutosaveLinkifiedTextarea, DatePicker, Select } from '@fikasio/react-ui-components';
import OBJECTIVE_STATUS_OPTIONS from 'components/objectives/objectiveStatusOptions';
import { objectiveStatus } from 'components/objectives/ObjectivesViewer/utils';
import ProjectsLabelsInput from 'components/projects/ProjectsLabelsInput';
import AssigneeButton from 'components/tasks/TasksViewer/AssigneeButton';
import TaskStatusDropdown from 'components/tasks/TasksViewer/TaskStatusDropdown';
import { statusChangePatch, visibleStatus } from 'components/tasks/TasksViewer/statusChange';
import { clearPaneContent } from 'services/pane/slice';
import { useAddObjectiveMutation, usePatchObjectiveMutation, useUpdateObjectiveMutation } from 'services/objectives/api';
import { Objective } from 'services/objectives/types';
import { useGetProjectsQuery } from 'services/projects/api';
import { pickedDateTimeInUserZone, toJSDate, toPickerDate } from 'utils/date';
import { preserveEmptyString, processFormData } from 'utils/forms';
import 'components/UI/Form.css';

const FieldError = ({
  error,
  touched,
}: {
  error?: unknown;
  touched?: boolean;
}) => (
  typeof error === 'string' && touched ? (
    <div className="text-danger" role="alert" style={{ marginTop: 4 }}>
      {error}
    </div>
  ) : null
);

const getSubmitError = (result: { error?: unknown }, fallback: string) => {
  if (!('error' in result) || !result.error) {
    return null;
  }
  return fallback;
};

interface ObjectiveInformationsSlots {
  actions: React.ReactNode;
  details: React.ReactNode;
  metadata: React.ReactNode;
  notices: React.ReactNode;
}

interface ObjectiveInformationsFormProps {
  children?: (slots: ObjectiveInformationsSlots) => React.ReactNode;
  className?: string;
  nameRef?: { current: string };
  objective?: Objective;
  onClose?: () => void;
  projectId?: string;
}

const READONLY_FIELDS = [
  'id',
  'archivedAt',
  'cancelledAt',
  'cancelReason',
  'completedAt',
  'createdAt',
  'isArchived',
  'modifiedAt',
  'progress',
  'status',
  'userId',
];

const ObjectiveInformationsForm = ({
  children,
  className,
  nameRef,
  objective,
  onClose,
  projectId,
}: ObjectiveInformationsFormProps) => {
  const descriptionId = useId();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data: projects } = useGetProjectsQuery();
  const [dueDate, setDueDate] = useState(
    toJSDate(objective?.dueDate) ?? null,
  );

  const [createObjective] = useAddObjectiveMutation();
  const [patchObjective] = usePatchObjectiveMutation();
  const [updateObjective] = useUpdateObjectiveMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const status = objectiveStatus(objective);

  const objectiveProjects = objective
    ? objective.projects
    : (projectId && projects?.find(p => p.id === projectId) ? [{ id: projectId }] : []);

  const descriptionTextarea = useRef<HTMLTextAreaElement>(null);
  const [descriptionTextareaHeight, setDescriptionTextareaHeight] = useState(3);

  const setTextareaHeight = (element: HTMLTextAreaElement) => {
    const rowHeight = 25;
    const maxNbRows = 10;
    const height = element.scrollHeight;
    const trows = Math.ceil(height / rowHeight) - 1;
    setDescriptionTextareaHeight(Math.min(maxNbRows, trows));
  };

  useEffect(() => {
    if (descriptionTextarea.current) {
      setTextareaHeight(descriptionTextarea.current);
    }
  }, []);

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextareaHeight(event.target);
  };

  const onSubmit = async (values: Record<string, any>) => {
    setSubmitError(null);
    const formData: any = processFormData({
      ...values,
      'number:goal': values['number:goal'] ?? null,
      'number:goalTolerance': values['number:goalTolerance'] ?? null,
      'number:initialProgress': values['number:initialProgress'] ?? null,
    });
    const goalKind = formData.goalKind || 'REACH';
    const payload = {
      ...omit(formData, READONLY_FIELDS),
      assignee: formData.assignee || null,
      goalKind,
      name: nameRef ? nameRef.current : formData.name,
      projects: (formData.projects || []).filter(p => p?.id),
    };
    if (payload.goal == null) {
      delete payload.goal;
    }
    if (payload.initialProgress == null) {
      delete payload.initialProgress;
    }
    if (goalKind === 'AROUND') {
      if (payload.goalTolerance == null) {
        delete payload.goalTolerance;
      }
    } else {
      payload.goalTolerance = null;
    }
    if (goalKind !== 'REACH') {
      payload.goalFrequency = null;
    } else if (!payload.goalFrequency) {
      delete payload.goalFrequency;
    }
    if (payload.dueDate instanceof Date) {
      payload.dueDate = DateTime.fromJSDate(payload.dueDate).toISO();
    }

    let result: { error?: unknown };
    try {
      result = objective?.id
        ? await updateObjective({
          id: objective.id,
          ...payload,
        })
        : await createObjective(payload);
    } catch {
      setSubmitError(t('invalidSubmission'));
      return;
    }
    const error = getSubmitError(result, t('invalidSubmission'));
    if (error) {
      setSubmitError(error);
      return;
    }
    dispatch(clearPaneContent());
  };

  return (
    <Form
      initialValues={{
        ...objective,
        goalKind: objective?.goalKind || 'REACH',
      }}
      keepDirtyOnReinitialize
      onSubmit={onSubmit}
      validate={values => {
        const errors: Record<string, string> = {};
        const name = nameRef ? nameRef.current : values.name;
        if (!name?.trim()) {
          errors.name = t('required');
        }
        if (!values.dueDate) {
          errors.dueDate = t('required');
        }
        if ((values.goalKind || 'REACH') === 'AROUND') {
          const goal = values['number:goal'];
          if (goal == null || goal === '') {
            errors['number:goal'] = t('required');
          }
        }
        return errors;
      }}
      render={({ errors, handleSubmit, submitFailed, values }) => {
        const goalKind = values.goalKind || 'REACH';
        const notices = (
          <>
            {
              submitError && (
                <Alert variant="danger">
                  {submitError}
                </Alert>
              )
            }
            {
              submitFailed && typeof errors?.name === 'string' && (
                <FieldError error={`${t('name')}: ${errors.name}`} touched />
              )
            }
          </>
        );
        const details = (
          <div>
            <label className="form-label" htmlFor={descriptionId}>{t('description')}</label>
            <Field
              name="description"
              parse={preserveEmptyString}
            >
              {
                ({ input }) => (
                  <AutosaveLinkifiedTextarea
                    id={descriptionId}
                    name="description"
                    onBlur={input.onBlur}
                    onChange={event => {
                      input.onChange(event);
                      handleDescriptionChange(event);
                    }}
                    onFocus={input.onFocus}
                    onSave={description => {
                      if (objective?.id) {
                        patchObjective({
                          description,
                          id: objective.id,
                        });
                      }
                    }}
                    ref={descriptionTextarea}
                    rows={descriptionTextareaHeight}
                    value={input.value ?? ''}
                  />
                )
              }
            </Field>
          </div>
        );
        const metadata = (
          <>
            <div>
              <label className="form-label">{t('goalKind')}</label>
              <Field name="goalKind">
                {({ input }) => (
                  <Select
                    defaultValue={input.value || 'REACH'}
                    onChange={value => input.onChange(value)}
                    options={[
                      { label: t('goalKindReach'), value: 'REACH' },
                      { label: t('goalKindHighest'), value: 'HIGHEST' },
                      { label: t('goalKindLowest'), value: 'LOWEST' },
                      { label: t('goalKindAround'), value: 'AROUND' },
                    ]}
                    style={{
                      width: '100%',
                    }}
                  />
                )}
              </Field>
            </div>
            <div>
              <label className="form-label">{goalKind === 'AROUND' ? t('target') : t('goal')}</label>
              <div
                style={{
                  alignItems: 'flex-end',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                <Field
                  className="form-control"
                  component="input"
                  defaultValue={objective?.goal}
                  name="number:goal"
                  parse={value => (value === '' ? null : value)}
                  style={{
                    flex: '0 0 100px',
                    width: 100,
                  }}
                />
                {
                  goalKind === 'REACH' && (
                    <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                      <Field
                        allowNull
                        component={
                          ({ input }) => (
                            <Select
                              defaultValue={input.value}
                              onChange={value => input.onChange(value)}
                              options={[
                                { label: '-', value: '' },
                                { label: t('daily'), value: 'DAILY' },
                                { label: t('weekly'), value: 'WEEKLY' },
                              ]}
                              style={{
                                width: '100%',
                              }}
                            />
                          )
                        }
                        name="goalFrequency"
                        parse={value => (value === '' ? null : value)}
                      />
                    </div>
                  )
                }
                {
                  goalKind === 'AROUND' && (
                    <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                      <label className="form-label" htmlFor="objective-goal-tolerance" style={{ marginTop: 0 }}>{t('goalTolerance')}</label>
                      <Field
                        aria-label={t('goalTolerance')}
                        className="form-control"
                        component="input"
                        defaultValue={objective?.goalTolerance ?? undefined}
                        id="objective-goal-tolerance"
                        name="number:goalTolerance"
                        parse={value => (value === '' ? null : value)}
                        style={{
                          width: '100%',
                        }}
                      />
                    </div>
                  )
                }
              </div>
              {
                submitFailed && errors?.['number:goal'] && (
                  <FieldError error={errors['number:goal']} touched />
                )
              }
            </div>
            <div>
              <label className="form-label">{t('deadline')} *</label>
              <Field
                component={
                  ({ input, meta }) => (
                    <>
                      <DatePicker
                        className="form-control"
                        defaultValue={toPickerDate(dueDate)}
                        displayFormat="yyyy-MM-dd"
                        onChange={date => {
                          const timestamp = pickedDateTimeInUserZone(date)
                            .set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
                          if (objective?.id) {
                            patchObjective({
                              id: objective.id,
                              dueDate: timestamp.toISO(),
                            });
                          }
                          setDueDate(timestamp.toJSDate());
                          input.onChange(timestamp.toJSDate());
                        }}
                      />
                      <FieldError error={meta.error} touched={meta.touched} />
                    </>
                  )
                }
                defaultValue={dueDate ?? undefined}
                name="dueDate"
              />
            </div>
            <div>
              <label className="form-label">{t('assignee')}</label>
              <Field name="assignee">
                {({ input }) => (
                  <AssigneeButton
                    assigneeId={input.value || undefined}
                    onChange={assignee => {
                      input.onChange(assignee);
                      if (objective?.id) {
                        patchObjective({
                          assignee,
                          id: objective.id,
                        });
                      }
                    }}
                  />
                )}
              </Field>
            </div>
            {
              goalKind === 'REACH' && (
                <div>
                  <label className="form-label">{t('initialProgress')}</label>
                  <Field
                    className="form-control"
                    component="input"
                    defaultValue={objective?.initialProgress}
                    name="number:initialProgress"
                    parse={value => (value === '' ? null : value)}
                    style={{ width: '100%' }}
                  />
                </div>
              )
            }
            <div>
              <label className="form-label">{t('projects')}</label>
              <Field
                defaultValue={objectiveProjects}
                name="projects"
              >
                {({ input }) => (
                  <ProjectsLabelsInput
                    onChange={nextProjects => {
                      const projectsToSave = (nextProjects || []).filter(project => project?.id);
                      input.onChange(projectsToSave);
                      if (objective?.id) {
                        patchObjective({
                          id: objective.id,
                          projects: projectsToSave,
                        });
                      }
                    }}
                    projects={projects}
                    value={input.value}
                  />
                )}
              </Field>
            </div>
            {
              objective?.id && (
                <div>
                  <label className="form-label">{t('status')}</label>
                  <div>
                    <TaskStatusDropdown
                      alignToggle="start"
                      onChange={(nextStatus, extras) => {
                        patchObjective({
                          id: objective.id,
                          ...statusChangePatch(nextStatus, {
                            cancelReason: extras?.cancelReason,
                            wasArchived: objective.isArchived,
                          }),
                        });
                      }}
                      status={visibleStatus(status, objective.isArchived)}
                      statusOptions={OBJECTIVE_STATUS_OPTIONS}
                    />
                  </div>
                  {
                    status === 'Cancelled' && objective.cancelReason && (
                      <div
                        style={{
                          color: '#6c757d',
                          marginTop: 8,
                        }}
                      >
                        {t('cancelReason')}: {objective.cancelReason}
                      </div>
                    )
                  }
                </div>
              )
            }
          </>
        );
        const actions = (
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={() => onClose && onClose()}
              style={{
                backgroundColor: 'white',
              }}
              variant="outline-secondary"
            >
              {t('cancel')}
            </Button>
            {
              objective
                ? <Button type="submit" variant="success">{t('save')}</Button>
                : <Button type="submit" variant="success">{t('create')}</Button>
            }
          </div>
        );
        return (
          <form
            className={className}
            onSubmit={handleSubmit}
          >
            {
              children
                ? children({ actions, details, metadata, notices })
                : (
                  <>
                    {notices}
                    {details}
                    {metadata}
                    {actions}
                  </>
                )
            }
          </form>
        );
      }}
    />
  );
};

export default ObjectiveInformationsForm;
